import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Loader2, ChevronRight } from "lucide-react";
import api, { fmtIDR } from "@/lib_app/api";

/** Picker for choosing the prospect a new quotation belongs to. */
export default function ProspectPickerDialog({ open, onClose, onPick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    setQ("");
    setLoading(true);
    api.get("/prospects")
      .then((r) => setRows((r.data || []).filter((p) => p.status !== "lost")))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((p) =>
      (p.customer || "").toLowerCase().includes(s) ||
      (p.service || "").toLowerCase().includes(s) ||
      (p.owner_name || "").toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-xl rounded-3xl border-0 p-0 overflow-hidden"
        data-testid="prospect-picker-dialog"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.97) 0%, rgba(232,240,254,0.94) 100%)",
          boxShadow: "0 32px 80px -20px rgba(59,74,99,0.30)",
        }}
      >
        <DialogTitle className="sr-only">Pilih Prospek</DialogTitle>
        <DialogDescription className="sr-only">Pilih prospek untuk penawaran baru</DialogDescription>

        <div className="p-6 pb-3">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#6B8FE2] font-bold mb-1">Penawaran Baru</p>
          <h2 className="text-xl font-bold text-[#090808]">Pilih Prospek</h2>
          <p className="text-xs text-[#A0A3A8] mt-1">Setiap penawaran harus terhubung ke satu prospek.</p>
          <div className="relative mt-4">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A3A8]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="prospect-picker-search"
              placeholder="Cari prospek / layanan / sales..."
              className="w-full h-11 pl-11 pr-3 rounded-full bg-white border border-[#DCE0E7] text-sm outline-none focus:ring-2 focus:ring-[#97ADD4]/40"
            />
          </div>
        </div>

        <div className="px-4 pb-5 max-h-[52vh] overflow-y-auto">
          {loading ? (
            <div className="py-12 text-center text-[#A0A3A8]"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-sm text-[#A0A3A8]">Tidak ada prospek yang cocok.</div>
          ) : (
            <div className="space-y-2">
              {filtered.map((p) => (
                <button
                  key={p.id}
                  onClick={() => onPick(p)}
                  data-testid={`prospect-picker-item-${p.id}`}
                  className="w-full text-left rounded-2xl bg-white border border-[#DCE0E7]/70 px-4 py-3 flex items-center gap-3 hover:shadow-md hover:border-[#B0C7EA] transition"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-[#090808] truncate">{p.customer}</div>
                    <div className="text-xs text-[#626061] truncate">{p.service || "-"}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-num font-bold text-[#3B4A63] tabular-nums">{fmtIDR(p.prospect_value)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold">{p.owner_nickname || p.owner_name || "-"}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#A0A3A8] shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
